/**
 * Which maneuvers are worth interrupting the driver for.
 *
 * Mapbox emits a step every time the road model changes, which on a Thai
 * highway means a run like this:
 *
 *    fork slight right   ref=304
 *    fork slight right   ref=304
 *    continue slight right ref=304
 *    fork slight right   ref=304 / AH19
 *
 * Announcing each one ("keep right to stay on 304", four times in 8 km) is
 * noise, and worse, it made the distance readout count down to a non-event so
 * the driver never learned how far the REAL turn was. The rule the driver
 * asked for: only speak up for a genuinely difficult turn, or when the road
 * number changes.
 *
 * Everything filtered out here still exists in the route - we just fold it
 * into the run leading to the next real maneuver, so the countdown reflects
 * the distance to something that actually needs a decision.
 */

import type { RouteStep } from '../core/types';
import { bearingDelta } from '../core/geo';
import { refChanges } from './roads';
import { lanesForStep, isLaneConstrained } from './lanes';

/** Maneuver types that always demand attention, whatever the road number does. */
const ALWAYS = new Set([
  'arrive',
  'roundabout',
  'rotary',
  'roundabout turn',
  'exit roundabout',
  'exit rotary',
  'end of road',
  'off ramp',
  'on ramp',
]);

/** Modifiers that are a real turn rather than a lane-keeping nudge. */
const HARD_MODIFIERS = new Set(['left', 'right', 'sharp left', 'sharp right', 'uturn']);

/** Beyond this heading change, a "slight" maneuver is really a turn. */
const HARD_TURN_DEGREES = 40;

/**
 * Is the maneuver that STARTS `step` worth announcing?
 * `prev` is the step we're travelling along as we approach it.
 */
export function isSignificant(step: RouteStep, prev: RouteStep | null): boolean {
  const m = step.maneuver;
  const type = (m.type ?? '').toLowerCase();
  const modifier = (m.modifier ?? '').toLowerCase();

  if (type === 'depart') return false;
  if (ALWAYS.has(type)) return true;
  if (HARD_MODIFIERS.has(modifier)) return true;

  // Everything left is a fork / continue / new name / slight nudge.

  // Changing route number is the driver's stated trigger.
  if (refChanges(prev, step)) return true;

  // A "slight" turn that swings more than HARD_TURN_DEGREES is not slight.
  if (Number.isFinite(m.bearing_before) && Number.isFinite(m.bearing_after)) {
    if (Math.abs(bearingDelta(m.bearing_before, m.bearing_after)) >= HARD_TURN_DEGREES) return true;
  }

  // If only some lanes on the approach stay on route, the driver MUST act -
  // this is the case that nearly sent us down the wrong left-hand lane.
  if (prev && isLaneConstrained(lanesForStep(prev))) return true;

  return false;
}

/** A maneuver that picks a branch of the road rather than turning across it. */
function isBranchManeuver(step: RouteStep): boolean {
  const type = (step.maneuver.type ?? '').toLowerCase();
  if (type === 'depart' || type === 'arrive') return false;
  const mod = (step.maneuver.modifier ?? '').toLowerCase();
  if (type === 'fork') return mod.includes('left') || mod.includes('right');
  return mod === 'slight left' || mod === 'slight right';
}

/**
 * A branch this close (metres) before a real maneuver is part of that
 * maneuver, not highway noise, and must be spoken.
 */
const BRANCH_SETUP_M = 1000;

/**
 * Precompute significance for every step of a leg-flattened route.
 * `steps[i]` significance describes the maneuver at the START of step i.
 *
 * On top of the per-step test, a PROMOTION pass: a fork that sits within
 * BRANCH_SETUP_M of the next significant maneuver is announced even though it
 * fails every isSignificant test. Reported from the road at a flyover on the
 * way to Williams: "the gps said turn right... theres a bridge with 2 lanes on
 * the right side and an underpass on the left, it said nothing about keeping
 * the left lane to go under the bridge, only turn right, which was impossible."
 * The keep-left onto the underpass parallels the flyover (no heading change),
 * keeps the same ref, and had no lane data - so the filter ate it, and the
 * first thing the driver heard was a turn that only exists under the bridge.
 * A branch choice that decides whether the upcoming turn is even reachable is
 * the opposite of noise; the 8-km highway "stay on 304" runs this filter was
 * built for have kilometres between fork and turn, so they stay folded.
 */
export function significanceMap(steps: RouteStep[]): boolean[] {
  const sig = steps.map((s, i) => isSignificant(s, i > 0 ? steps[i - 1] : null));

  // Distance from each maneuver to the next ORIGINALLY-significant one -
  // measured against the pre-promotion set so promotions cannot cascade into
  // a chain of announced forks.
  let toNext = Infinity;
  const promoted = [...sig];
  for (let i = steps.length - 1; i >= 0; i--) {
    const afterStep = i + 1 < steps.length && sig[i + 1] ? 0 : toNext;
    toNext = steps[i].distance + afterStep;
    if (i > 0 && !sig[i] && toNext <= BRANCH_SETUP_M && isBranchManeuver(steps[i])) {
      promoted[i] = true;
    }
  }
  return promoted;
}
